import { Rotate90DegreesCcw } from "@mui/icons-material";
import VideocamIcon from "@mui/icons-material/Videocam";
import {useSizingContext} from '../../../Context/SizingContext'

import {
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import * as React from "react";
import { useRef, useContext } from "react";
import { Context } from "../../../Context/context";
import { isMobile } from "react-device-detect";
import { useTimeoutWhen } from "rooks";
import SimpleDialog from "../../Controls/SimpleDialog";
import { THREE_INST } from "../../SketchUtilities/Three/threeSingleton";
import { MOVIE_MAKER } from "./MovieMaker";

const movieMaker = MOVIE_MAKER.getInst();
const three = THREE_INST.getInst();

const MovieMaker = () => {
  const { state:{ isSmallScreen, isPortrait } } = useSizingContext();

  const [canStartRecording, setCanStartRecording] = React.useState(false);
  const [openPreviewModal, setOpenPreviewModal] = React.useState(false);
  const [currentMMStatus, setCurrentMMStatus] = React.useState("STOPPED");
  const [openStartDialog, setOpenStartDialog] = React.useState(false);
  const { state, dispatch } = useContext(Context);
  const [title, setTitle] = React.useState("");
  const [length, setLength] = React.useState(15);
  const progress = useRef(0);
  const [error, setError] = React.useState(false);


  
  React.useEffect(() => {
    if (
      movieMaker.obj.eventHandler === undefined ||
      movieMaker.obj.setProgress === undefined
    ) {
      movieMaker.obj.eventHandler = MovieMakerEventHandler;
      movieMaker.obj.setProgress = setProgress;
    }
  }, []);

  React.useEffect(() => {
    three.obj.doNotRender = openPreviewModal || openStartDialog;
  }, [openPreviewModal, openStartDialog]);

  const setProgress = (val: number) => {
    progress.current = val;
  };

  const MovieMakerEventHandler = (event: string, arg?: any) => {
    setCurrentMMStatus(event);
  };

  useTimeoutWhen(
    () => {
      movieMaker.obj.stopRecording();
      setCanStartRecording(false);
      setOpenPreviewModal(true);
      setCurrentMMStatus("SHOW_PREVIEW");
    },
    length * 1000,
    canStartRecording
  );

  useTimeoutWhen(
    () => {
      handlePreviewDisplay();
    },
    1000,
    openPreviewModal
  );

  useTimeoutWhen(
    () => {
      setOpenPreviewModal(false);
    },
    1000,
    currentMMStatus === "COMPLETE"
  );

  const startRecordingButton = () => {
    return (
      <div
        style={{flex:"1"}}
        className="recordButton"
        onClick={() => {
          if (openPreviewModal || openStartDialog) return;
          setOpenStartDialog(true);
        }}
      >
        <VideocamIcon
          className={currentMMStatus === "RECORDING" ? "recordingActive" : ""}
          style={{ width: "40px", height: "40px" }}
        />
        Record!
      </div>
    );
  };

  const handlePreviewDisplay = () => {
    const canvas = document.getElementById(
      "previewVidCanvas"
    ) as HTMLCanvasElement;
    const ctx = canvas.getContext("2d");
    movieMaker.obj.displayPreview(ctx!, canvas.width, canvas.height);
  };

  const previewContent = () => {
    return (
      <div
        style={
          isMobile && isPortrait && isSmallScreen
            ? {
                display: "flex",
                width: "100%",
                height: "50vh",
                justifyContent: "center",
                alignItems: "center",
              }
            : {
                display: "flex",
                width: "100%",
                justifyContent: "center",
                alignItems: "center",
                overflow: "hidden",
              }
        }
      >
        {currentMMStatus === "SHOW_PREVIEW" && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              left: "0",
              top: "40%",
              right: "0",
              position: "absolute",
            }}
          >
            <CircularProgress color="inherit" />
          </div>
        )}
        <canvas
          id="previewVidCanvas"
          style={{
            display: "block",
            border: "1px solid black",
            transform: isMobile && isPortrait && isSmallScreen ? "rotate(90deg)" : "",
          }}
        ></canvas>
      </div>
    );
  };

  const startDialogComponent = () => {
    return (
      <Grid container spacing={3} style={{ marginTop: "2px" }}>
        <Grid item xs={12}>
          <FormControl>
            <TextField
              fullWidth
              id="outlined-basic"
              label="Your movie needs a title"
              variant="outlined"
              error={error}
              onChange={(e) => {
                if (e.target.value.length > 0) {
                  setError(false);
                }
                setTitle(e.currentTarget.value);
              }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl>
            <FormLabel>Recording Length</FormLabel>
            <RadioGroup
              row
              defaultValue="15"
              onChange={(e) => {
                setLength(parseInt(e.currentTarget.value));
              }}
            >
              <FormControlLabel value="15" control={<Radio />} label="15s" />
              <FormControlLabel value="30" control={<Radio />} label="30s" />
              <FormControlLabel value="45" control={<Radio />} label="45s" />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>
    );
  };

  const handleClosePreviewModal = () => {
    setOpenPreviewModal(false);

    movieMaker.obj.cleanup();
  };

  const handleCloseStartModal = () => {
    setOpenStartDialog(false);
    movieMaker.obj.cleanup();
  };

  const previewModal = () => {
    return SimpleDialog({
      open: openPreviewModal,
      title: "See your art in action!",
      content: previewContent(),
      actionTitle: "Download",
      actionUseLoading: true,
      actionIsLoading: currentMMStatus === "DOWNLOADING",
      actionCB: movieMaker.obj.initDownload,
      disableClose: currentMMStatus === "DOWNLOADING",
      handleClose: handleClosePreviewModal,
    });
  };

  const startRecordingDialog = () => {
    return SimpleDialog({
      open: openStartDialog,
      title: "Select a title and recording length!",
      content: startDialogComponent(),
      actionTitle: "Start",
      actionCB: () => {
        if (title.length === 0) {
          setError(true);
          return;
        }
        const canvasTarget = document.getElementById(
          "threeCanvasEl"
        ) as HTMLCanvasElement;

        const background = three.obj.getCurrentImage();
        setCurrentMMStatus("RECORDING");
        setCanStartRecording(true);
        setOpenStartDialog(false);
        movieMaker.obj.init(
          canvasTarget,
          length,
          title,
          background.image,
          true,
          state.movieAuth
        );
      },
      handleClose: handleCloseStartModal,
    });
  };

  return (
    <>
      {startRecordingButton()}
      {previewModal()}
      {startRecordingDialog()}
    </>
  );
};

export default MovieMaker;
